import React from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Alert from '@mui/material/Alert';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { DateTimePicker } from '@mui/x-date-pickers';
import { Icon } from '@iconify/react';
import dayjs from 'dayjs';
import Iconify from '../../../../components/iconify';
import { Divider, SaveButton } from '../components';
import { PROFILE_FORM_EMPTY_STATE_SX, PROFILE_FORM_NESTED_CARD_SX } from '../profileFormStyles';

/**
 * Certificates section of the profile form
 * Handles: certificates management (add/remove/display)
 * 
 * Has two display modes:
 * 1. Editable (sectionSave modes) - can add/remove certificates
 * 2. Read-only (showSkillkoCerts) - displays Skillko certificates as cards
 */
const CertificatesSection = ({
  config,
  profileData,
  metadata,
  errors,
  
  // Certificate form state
  selectedCertificate,
  setSelectedCertificate,
  certificateValidFrom,
  setCertificateValidFrom,
  certificateExpiresAt,
  setCertificateExpiresAt,
  certificateErrors,
  certificateNumber,
  setCertificateNumber,
  
  // Handlers
  handleAddCertificate,
  handleRemoveCertificate,
  handleAddNewCertificateType,
  onSave,
  isEdited,
  canEdit,
}) => {
  const { sectionSave } = config;
  const sectionConfig = config.sections.certificates;
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const gridSpacing = isSmUp ? 3 : 2;
  
  // For edit mode, check if profile is from Skillko (certificates not editable)
  // Also disable if user can't edit (e.g., deleted profiles)
  const isSkillkoProfile = profileData.source === 'Skillko';
  const certificatesDisabled = isSkillkoProfile || !sectionConfig.editable || !canEdit;

  const isCertificateCreated = (value) => {
    return !!metadata.certificates?.find(x => x.name === value);
  };

  // Render Skillko certificates (read-only display)
  const renderSkillkoCertificates = () => {
    const skillkoCerts = profileData.skillkoCertificates || [];
    
    if (skillkoCerts.length === 0) {
      return (
        <Box sx={PROFILE_FORM_EMPTY_STATE_SX}>
          <Typography variant="body2" color="textSecondary">
            No certificates available from Skillko
          </Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={2}>
        {[...skillkoCerts]
          .sort((a, b) => dayjs(b.expiresAt).valueOf() - dayjs(a.expiresAt).valueOf())
          .map((cert, index) => {
            const isExpired = dayjs(cert.expiresAt).isBefore(dayjs());
            
            return (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card sx={PROFILE_FORM_NESTED_CARD_SX}>
                  {isExpired && (
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 8,
                        right: 8,
                        backgroundColor: 'error.main',
                        color: 'common.white',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        zIndex: 1,
                        textTransform: 'uppercase'
                      }}
                    >
                      Expired
                    </Box>
                  )}
                  <Typography 
                    variant="subtitle1" 
                    sx={{ fontWeight: 600, mb: 1.5, fontSize: { xs: '0.95rem', sm: '1rem' } }}
                  >
                    {cert.name}
                  </Typography>
                  
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                      Valid From
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}>
                      {dayjs(cert.validFrom).format('DD/MM/YYYY')}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                      Expires At
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}>
                      {dayjs(cert.expiresAt).format('DD/MM/YYYY')}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                      Status
                    </Typography>
                    <Typography 
                      variant="body2"
                      sx={{ 
                        fontSize: { xs: '0.85rem', sm: '0.875rem' },
                        fontWeight: 500,
                        color: cert.approvalStatus === 'approved' ? 'success.main' : 'text.secondary'
                      }}
                    >
                      {cert.approvalStatus ? cert.approvalStatus.charAt(0).toUpperCase() + cert.approvalStatus.slice(1) : 'Not Set'}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            );
          })}
      </Grid>
    );
  };

  // Render editable certificates (Create/Edit modes)
  const renderEditableCertificates = () => {
    const certificates = profileData.certificates || [];
    
    return (
      <>
        {isSkillkoProfile && (
          <Alert severity="info" sx={{ mb: 3 }}>
            This profile is managed through Skillko and certificates cannot be edited here. 
            Any changes must be made in Skillko.
          </Alert>
        )}
        
        {/* Add Certificate Form */}
        <Grid container spacing={2} sx={{ mb: 3 }} alignItems="stretch">
          <Grid item sm={3} xs={12}>
            <Autocomplete
              disabled={certificatesDisabled}
              options={metadata.certificates.filter(cert => 
                !certificates.some(selected => (selected.typeId || selected.id) === cert.id)
              )}
              getOptionLabel={(option) => option.name}
              value={selectedCertificate}
              onChange={(event, newValue) => setSelectedCertificate(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Certificate"
                  error={!!certificateErrors.certificate}
                  helperText={certificateErrors.certificate || ''}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {params.inputProps.value.length > 3 && !isCertificateCreated(params.inputProps.value) && !certificatesDisabled && (
                          <IconButton 
                            color="primary"
                            onClick={() => handleAddNewCertificateType(params.inputProps.value)}
                          >
                            <Iconify icon="eva:plus-fill" width={25} />
                          </IconButton>
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid item sm={2} xs={12}>
            <TextField
              fullWidth
              disabled={certificatesDisabled}
              label="Number (Optional)"
              value={certificateNumber}
              onChange={(e) => setCertificateNumber(e.target.value)}
            />
          </Grid>
          <Grid item sm={3} xs={12}>
            <DateTimePicker
              disabled={certificatesDisabled}
              label="Valid From"
              value={certificateValidFrom}
              onChange={(value) => setCertificateValidFrom(value)}
              sx={{ width: '100%' }}
              format="DD/MM/YYYY HH:mm"
              ampm={false}
              slotProps={{
                textField: {
                  error: !!certificateErrors.validFrom,
                  helperText: certificateErrors.validFrom || '',
                },
              }}
            />
          </Grid>
          <Grid item sm={3} xs={12}>
            <DateTimePicker
              disabled={certificatesDisabled}
              label="Expires At"
              value={certificateExpiresAt}
              onChange={(value) => setCertificateExpiresAt(value)}
              sx={{ width: '100%' }}
              format="DD/MM/YYYY HH:mm"
              ampm={false}
              slotProps={{
                textField: {
                  error: !!certificateErrors.expiresAt,
                  helperText: certificateErrors.expiresAt || '',
                },
              }}
            />
          </Grid>
          <Grid item sm={1} xs={12} sx={{ display: 'flex' }}>
            <Button
              variant="contained"
              fullWidth
              disabled={certificatesDisabled}
              onClick={handleAddCertificate}
              startIcon={<Icon icon="eva:plus-fill" />}
              sx={{ height: { xs: '48px', sm: '56px' }, minHeight: '48px' }}
            >
              Add
            </Button>
          </Grid>
        </Grid>

        {/* Certificate Cards */}
        {certificates.length === 0 ? (
          <Box sx={PROFILE_FORM_EMPTY_STATE_SX}>
            <Typography variant="body2" color="textSecondary">
              No certificates added yet
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {[...certificates]
              .sort((a, b) => {
                const aDate = a.expiresAt?.valueOf?.() || dayjs(a.expiresAt).valueOf();
                const bDate = b.expiresAt?.valueOf?.() || dayjs(b.expiresAt).valueOf();
                return bDate - aDate;
              })
              .map((cert, index) => {
                const expiresAt = cert.expiresAt?.format ? cert.expiresAt : dayjs(cert.expiresAt);
                const validFrom = cert.validFrom?.format ? cert.validFrom : dayjs(cert.validFrom);
                const isExpired = expiresAt.isBefore(dayjs());
                
                return (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card sx={PROFILE_FORM_NESTED_CARD_SX}>
                      {isExpired && (
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 8,
                            right: 8,
                            backgroundColor: 'error.main',
                            color: 'common.white',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            zIndex: 1,
                            textTransform: 'uppercase'
                          }}
                        >
                          Expired
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography 
                          variant="subtitle1" 
                          sx={{ fontWeight: 600, pr: 1, fontSize: { xs: '0.95rem', sm: '1rem' } }}
                        >
                          {cert.name}
                        </Typography>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={certificatesDisabled}
                          onClick={() => handleRemoveCertificate(cert)}
                          sx={{ mt: -0.5, mr: -0.5 }}
                        >
                          <Icon icon="eva:trash-2-outline" width={20} />
                        </IconButton>
                      </Box>
                      
                      {cert.number && (
                        <Box sx={{ mb: 1.5 }}>
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                            Number
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}>
                            {cert.number}
                          </Typography>
                        </Box>
                      )}
                      
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                          Valid From
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}>
                          {validFrom.format('DD/MM/YYYY HH:mm')}
                        </Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                          Expires At
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}>
                          {expiresAt.format('DD/MM/YYYY HH:mm')}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                );
              })}
          </Grid>
        )}

        {/* Save button for section save mode */}
        {sectionSave && (
          <Grid 
            container 
            justifyContent={{ xs: 'stretch', sm: 'flex-end' }} 
            spacing={2}
            sx={{ mt: { xs: 1.5, sm: 2 } }}
          >
            <Grid item>
              <SaveButton 
                disabled={certificatesDisabled || !isEdited} 
                onClick={onSave}
                sectionSave={sectionSave}
                canEdit={canEdit}
              />
            </Grid>
          </Grid>
        )}
      </>
    );
  };

  return (
    <>
      <Grid item xs={12} marginBottom={{ xs: 2, sm: 3 }} marginTop={{ xs: 2, sm: 3 }}>
        <Divider text={sectionConfig.dividerText} />
      </Grid>

      <Grid container spacing={gridSpacing} data-testid="certificates-section">
        <Grid item xs={12} sx={{ mt: { xs: 1, sm: 2 } }}>
          <Typography 
            variant="subtitle1" 
            sx={{ mb: { xs: 1.5, sm: 2 }, fontSize: { xs: '0.95rem', sm: '1rem' } }}
          >
            Certificates
          </Typography>
          
          {sectionConfig.showSkillkoCerts
            ? renderSkillkoCertificates()
            : renderEditableCertificates()
          }
        </Grid>
      </Grid>
    </>
  );
};

export default CertificatesSection;

